/**
 * Route guard, used as a layout route.
 *
 *     { element: <ProtectedRoute permission="organization.view_site" />,
 *       children: [...] }
 *     { element: <ProtectedRoute role="EXECUTIVE" />, children: [...] }
 *
 * Lacking a permission renders a 403 page rather than redirecting: bouncing
 * the user somewhere else hides *why* they cannot see the screen, and if the
 * destination is also protected it loops.
 *
 * `role` checks a role code from `/auth/me/`. A global scope (superuser) is
 * allowed through any role guard so the emergency account can reach every
 * screen for support — the underlying module permissions still apply.
 */
import { Navigate, Outlet, useLocation } from 'react-router';

import type { ViewKey } from '@/config/views';
import { useCanSee, useHasRole, usePermission, useScope } from '@/shared/hooks/usePermission';
import { Forbidden } from '@/shared/components/Forbidden';
import { FullPageLoader } from '@/shared/components/FullPageLoader';
import { useAuth } from '../hooks/useAuth';

interface Props {
  view?: ViewKey;
  permission?: string | undefined;
  role?: string | undefined;
}

export function ProtectedRoute({ view, permission, role }: Props) {
  const { isAuthenticated, isRestoring } = useAuth();
  const location = useLocation();
  const allowed = usePermission(permission ?? '');
  const canSee = useCanSee(view);
  const hasRole = useHasRole(role ?? '');
  const { isGlobal } = useScope();

  // Must wait: rendering the redirect before the session is restored would
  // bounce every authenticated user to the login screen on a page refresh.
  if (isRestoring) return <FullPageLoader />;

  if (!isAuthenticated) {
    // `state.from` lets the login screen return the user where they meant to go.
    return <Navigate to="/ingresar" replace state={{ from: location }} />;
  }

  if (view && !canSee) return <Forbidden />;
  if (permission && !allowed) return <Forbidden />;
  if (role && !hasRole && !isGlobal) return <Forbidden />;

  return <Outlet />;
}
